/** Injected into react-to-print so wide tables fit the PDF page neatly. */
export const APR_PRINT_PAGE_STYLE = `
  @page {
    size: A4 landscape;
    margin: 10mm 8mm;
  }

  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  body {
    font-family: "Segoe UI", Tahoma, Geneva, Verdana, Arial, sans-serif !important;
    font-size: 9.5pt !important;
    line-height: 1.35 !important;
    color: #111 !important;
  }

  .apr-report-print-root,
  .apr-ws-content,
  .apr-ws-table-host {
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .no-print,
  .apr-author-meta,
  .apr-view-toggle__toolbar,
  .ant-btn,
  button:not(.keep-print) {
    display: none !important;
  }

  .card,
  .card-body,
  .card-header,
  .col-12,
  .row,
  .gutters {
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .card {
    border: 1px solid #ccc !important;
    box-shadow: none !important;
    break-inside: avoid;
    page-break-inside: avoid;
    margin-bottom: 10px !important;
  }

  .card-header {
    background: #eef2f6 !important;
    font-weight: 600 !important;
    padding: 6px 8px !important;
  }

  .card-body {
    padding: 6px 8px !important;
  }

  .table-responsive,
  .screen-view-tables,
  [class*="overflow"],
  .overflow-x-auto,
  .overflow-auto {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
    border: none !important;
    box-shadow: none !important;
  }

  table,
  table.table,
  table.table-bordered {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
    font-size: 8pt !important;
    line-height: 1.25 !important;
    page-break-inside: auto;
  }

  thead {
    display: table-header-group;
  }

  tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  th, td {
    border: 1px solid #999 !important;
    padding: 3px 4px !important;
    vertical-align: top !important;
    word-wrap: break-word !important;
    overflow-wrap: anywhere !important;
    white-space: normal !important;
    font-size: 8pt !important;
  }

  th {
    background: #e8eef4 !important;
    font-weight: 600 !important;
    color: #16325a !important;
  }

  h2, h3, h4 {
    color: #16325a !important;
    page-break-after: avoid;
    break-after: avoid;
  }

  h2 {
    font-size: 13pt !important;
    text-align: center !important;
    margin: 12px 0 8px !important;
  }

  h2.apr-chapter-heading {
    page-break-before: always;
  }

  h2.apr-chapter-heading:first-of-type {
    page-break-before: auto;
  }

  h3 {
    font-size: 11pt !important;
    margin: 10px 0 6px !important;
  }

  .apr-chapter-intro {
    text-align: center !important;
    max-width: 100% !important;
    margin: 0 auto 10px !important;
    font-size: 9pt !important;
  }

  .apr-chart-wrap,
  .apr-chart-wrap--pie {
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  canvas, img, svg {
    max-width: 100% !important;
    height: auto !important;
  }
`;

/** Expand scroll wrappers so react-to-print captures full table width. */
export function prepareAprPrintRoot(rootEl) {
  if (!rootEl) return;

  const scrollNodes = rootEl.querySelectorAll(
    ".table-responsive, .screen-view-tables, .overflow-x-auto, .overflow-auto, [style*='overflow']",
  );

  scrollNodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.dataset.aprPrevOverflow = node.style.overflow || "";
    node.dataset.aprPrevOverflowX = node.style.overflowX || "";
    node.dataset.aprPrevMaxWidth = node.style.maxWidth || "";
    node.dataset.aprPrevWidth = node.style.width || "";
    node.style.overflow = "visible";
    node.style.overflowX = "visible";
    node.style.maxWidth = "100%";
    node.style.width = "100%";
  });

  const tables = rootEl.querySelectorAll("table");
  tables.forEach((table) => {
    if (!(table instanceof HTMLElement)) return;
    table.dataset.aprPrevMinWidth = table.style.minWidth || "";
    table.dataset.aprPrevWidth = table.style.width || "";
    table.dataset.aprPrevTableLayout = table.style.tableLayout || "";
    table.style.minWidth = "0";
    table.style.width = "100%";
    table.style.tableLayout = "fixed";
  });
}

export function restoreAprPrintRoot(rootEl) {
  if (!rootEl) return;

  const scrollNodes = rootEl.querySelectorAll(
    ".table-responsive, .screen-view-tables, .overflow-x-auto, .overflow-auto, [style*='overflow'], [data-apr-prev-overflow]",
  );

  scrollNodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if ("aprPrevOverflow" in node.dataset) {
      node.style.overflow = node.dataset.aprPrevOverflow;
      delete node.dataset.aprPrevOverflow;
    }
    if ("aprPrevOverflowX" in node.dataset) {
      node.style.overflowX = node.dataset.aprPrevOverflowX;
      delete node.dataset.aprPrevOverflowX;
    }
    if ("aprPrevMaxWidth" in node.dataset) {
      node.style.maxWidth = node.dataset.aprPrevMaxWidth;
      delete node.dataset.aprPrevMaxWidth;
    }
    if ("aprPrevWidth" in node.dataset) {
      node.style.width = node.dataset.aprPrevWidth;
      delete node.dataset.aprPrevWidth;
    }
  });

  const tables = rootEl.querySelectorAll("table[data-apr-prev-min-width], table[data-apr-prev-width]");
  tables.forEach((table) => {
    if (!(table instanceof HTMLElement)) return;
    if ("aprPrevMinWidth" in table.dataset) {
      table.style.minWidth = table.dataset.aprPrevMinWidth;
      delete table.dataset.aprPrevMinWidth;
    }
    if ("aprPrevWidth" in table.dataset) {
      table.style.width = table.dataset.aprPrevWidth;
      delete table.dataset.aprPrevWidth;
    }
    if ("aprPrevTableLayout" in table.dataset) {
      table.style.tableLayout = table.dataset.aprPrevTableLayout;
      delete table.dataset.aprPrevTableLayout;
    }
  });
}
